import range from 'lodash/range';
import cx from 'clsx';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import useTranslation from 'next-translate/useTranslation';
import styles from './SearchControls.module.scss';

export const SearchControlsPaginationPropTypes = {
  onPageChange: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  numPages: PropTypes.number.isRequired
};

export function SearchControlsPagination({ onPageChange, currentPage, numPages }) {
  const NUM_SURROUNDING_PAGES = 3;

  const applyBounds = (min, val, max) => {
    const tmp = Math.max(min, val);
    return Math.min(tmp, max);
  };

  const lastPage = numPages - 1;

  const isFirst = currentPage <= 0;
  const isLast = currentPage >= lastPage;
  const WINDOW_SIZE = 4;

  let windowStart = currentPage - Math.floor(WINDOW_SIZE / 2);
  windowStart = Math.max(0, windowStart);

  let windowEnd = windowStart + WINDOW_SIZE - 1;

  if (windowEnd > lastPage) {
    windowEnd = lastPage;
    windowStart = Math.max(0, windowEnd - WINDOW_SIZE + 1);
  }

  const pageWindow = range(windowStart, windowEnd + 1);
  return (
    <div className={styles.paginationControls}>
      {!isFirst && (
        <button
          className={cx(styles.pageChanger, styles.leftcontrolButton)}
          type="button"
          onClick={() => onPageChange(currentPage - 1)}>
        </button>
      )}

      {pageWindow.map((pageNumber) => {
        return (
          <button
            key={pageNumber}
            type="button"
            className={cx(styles.pageChanger, styles.controlButton, { [styles.selected]: pageNumber === currentPage })}
            onClick={() => onPageChange(pageNumber)}>
            {pageNumber + 1}
          </button>
        );
      })}

      {!isLast && (
        <button
          className={cx(styles.pageChanger, styles.rightcontrolButton)}
          type="button"
          onClick={() => onPageChange(currentPage + 1)}>
        </button>
      )}
    </div>
  );
}

export const SearchControlsPageSizePropTypes = {
  onPageSizeChange: PropTypes.func.isRequired,
  currentPageSize: PropTypes.number.isRequired
};

export function SearchControlsPageSize({ onPageSizeChange, currentPageSize }) {
  const { t } = useTranslation();
  const pageSizes = [10, 25, 50, 100];

  return (
    <div className={styles.pageSizeControls}>
      <p className={styles.pageSizeText}>{t('common:searchResultsPerPage')}</p>
      {pageSizes.map((size) => (
        <button
          key={size}
          type="button"
          onClick={() => {
            onPageSizeChange(size);
          }}
          className={cx(styles.pageSizeChanger, styles.controlButton, { [styles.selected]: size === currentPageSize })}>
          {size}
        </button>
      ))}
    </div>
  );
}

export const SearchControlsPropTypes = {
  onPageSizeChange: PropTypes.func.isRequired,
  currentPageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  numPages: PropTypes.number.isRequired
};

export function SearchControls({ onPageSizeChange, currentPageSize, onPageChange, currentPage, numPages }) {
  // Reset to the first page if the current page size ever changes
  useEffect(() => {
    onPageChange(0);
  }, [currentPageSize]);

  return (
    <div data-testid="SearchControls" className={styles.searchControls}>
      <SearchControlsPagination {...{ onPageChange, currentPage, numPages }} />
    </div>
  );
}

SearchControlsPagination.propTypes = SearchControlsPaginationPropTypes;

SearchControlsPageSize.propTypes = SearchControlsPageSizePropTypes;

SearchControls.propTypes = SearchControlsPropTypes;
